import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EntityCollectionServiceElementsFactory } from '@ngrx/data';
import {
  ErrorInterface,
  NewSiteInterface,
  NodeImportInterface,
  SiteInterface,
  SiteShareTypeEnum,
} from '@shared/api';
import { environment } from '@shared/environments';
import {
  FormButtonTypeEnum,
  FormControlTypeEnum,
  FormService,
} from '@shared/form';
import { ModalsService } from '@shared/ui/modals';
import { ServiceCodePipe } from '@shared/ui/pipes';
import { isString } from 'lodash-es';
import { ToastrService } from 'ngx-toastr';
import { catchError, Observable, of, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { UserDataStore } from '../../user';
import { CustomEntityCollectionBaseService } from '../entity-collection-base.class';
import { EntityTypeEnum } from '../entity.interface';
import {
  AddSiteModalComponent,
  AddSiteModalDataInterface,
  ImportNodesModalComponent,
  ImportNodesModalDataInterface,
} from './index';

@Injectable({
  providedIn: 'root',
})
export class SitesService extends CustomEntityCollectionBaseService<SiteInterface> {
  private readonly modalsService = inject(ModalsService);
  private readonly toastrService = inject(ToastrService);
  private readonly userDataStore = inject(UserDataStore);
  private readonly formService = inject(FormService);
  private readonly http = inject(HttpClient);

  constructor(elementsFactory: EntityCollectionServiceElementsFactory) {
    super(EntityTypeEnum.Site, elementsFactory);
  }

  confirmRights(
    site: SiteInterface | string,
    data: { login: string; password: string },
  ): Observable<unknown> {
    return this.connectNode(site, data).pipe(tap(() => this.refresh()));
  }

  connectNode(
    site: SiteInterface | string,
    data: { login: string; password: string },
  ): Observable<unknown> {
    return this.http
      .patch(
        `${environment.apiHost}/sites/${isString(site) ? site : site.guid}/confirm-right`,
        data,
      )
      .pipe(tap(() => this.refresh()));
  }

  updateSites(sites: SiteInterface[] | string[]): Observable<unknown> {
    let ids: string[];

    if (isString(sites[0])) {
      ids = sites as string[];
    } else {
      ids = (sites as SiteInterface[]).map((site) => site.guid);
    }

    return this.http
      .post(`${environment.apiHost}/sites/run-os-update-command`, {
        items: ids.map((id) => ({
          guid: id,
        })),
      })
      .pipe(
        tap((response: any) => {
          this.refresh();
          this.handleOperationError(response['errors'] ?? []);
        }),
      );
  }

  private handleOperationError(errors: ErrorInterface[]): void {
    const unknownErrorMessage = $localize`:@@nodesUnknownErrorText:Unknown error. Please try again or contact our <a href="https://${environment.vendor?.supportUrl}:VENDOR_SUPPORT_URL:" target="_blank">Support</a>`;

    errors.forEach((item: ErrorInterface) => {
      this.toastrService.error(item.message || unknownErrorMessage);
    });
  }

  renameSite(site: SiteInterface, name: string): Observable<SiteInterface> {
    return this.update(
      {
        guid: site.guid,
        name: name,
      },
      { isOptimistic: true },
    );
  }

  rebootSites(
    sites: SiteInterface[],
  ): Observable<{ errors?: ErrorInterface[] }> {
    return this.http
      .post<{ errors?: ErrorInterface[] }>(
        `${environment.apiHost}/sites/run-reboot-command`,
        {
          items: sites.map((site) => ({
            guid: site.guid,
          })),
        },
      )
      .pipe(
        tap((response) => {
          this.refresh();
          this.handleOperationError(response['errors'] ?? []);
        }),
      );
  }

  markSitesAsPublic(sites: SiteInterface[]): Observable<SiteInterface> {
    const body = {
      siteGuids: sites.map((site) => site.guid),
      isPrivate: false,
    };

    this.updateManyInCache(
      sites.map((site) => ({
        ...site,
        isPrivate: false,
        shareType: SiteShareTypeEnum.SHARED_BY_ME,
      })),
    );

    return this.http
      .put<SiteInterface>(`${environment.apiHost}/sites/change-privacy`, body)
      .pipe(tap(() => this.refresh()));
  }

  markSitesAsPrivate(sites: SiteInterface[]): Observable<unknown> {
    const body = {
      siteGuids: sites.map((site) => site.guid),
      isPrivate: true,
    };

    this.updateManyInCache(
      sites.map((site) => ({
        ...site,
        isPrivate: true,
        shareType: SiteShareTypeEnum.PRIVATE,
      })),
    );

    return this.http
      .put(`${environment.apiHost}/sites/change-privacy`, body)
      .pipe(tap(() => this.refresh()));
  }

  deleteSites(sites: SiteInterface[]): Observable<unknown> {
    const body = {
      siteGuids: sites.map((site) => site.guid),
    };

    this.removeManyFromCache(sites);
    this.userDataStore.decreaseSitesCountOptimistically(sites.length);

    return this.http
      .put(`${environment.apiHost}/sites/delete`, body)
      .pipe(tap(() => this.refresh()));
  }

  openAddSiteModal(): void {
    this.modalsService
      .open<AddSiteModalComponent, AddSiteModalDataInterface>(
        AddSiteModalComponent,
        {
          data: {
            addSite: (value) =>
              this.http.put(`${environment.apiHost}/sites`, value).pipe(
                tap(() => {
                  this.showSuccessNotification(value);
                  this.userDataStore.increaseSitesCountOptimistically(1);
                }),
                catchError((error: { status: number }) => {
                  if (error.status === 409) {
                    this.openTransferSiteModal(value);
                    return of(true);
                  } else {
                    return throwError(() => error);
                  }
                }),
              ),
          },
        },
      )
      .afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this.refresh();
        }
      });
  }

  // TODO: Move to application level when sidebar is refactored
  openImportNodesModal(): void {
    this.modalsService
      .open<ImportNodesModalComponent, ImportNodesModalDataInterface>(
        ImportNodesModalComponent,
        {
          data: {
            nodes: this.loadAvailableNodes$(),
            action: (nodesForImport: NodeImportInterface[]) =>
              this.importNodes$(nodesForImport),
          },
          panelClass: 'base-modal-wide',
          width: '600px',
        },
      )
      .afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this.refresh();
        }
      });
  }

  private loadAvailableNodes$(): Observable<NodeImportInterface[]> {
    const httpParams = new HttpParams().append('pagination', 'false');

    return this.http
      .get<NodeImportInterface[]>(`${environment.apiHost}/warranty-devices`, {
        params: httpParams,
      })
      .pipe(map((nodes) => nodes.sort((a) => (a.alreadyAdded ? 1 : -1))));
  }

  private importNodes$(
    nodesForImport: NodeImportInterface[],
  ): Observable<unknown> {
    return this.http
      .post(`${environment.apiHost}/warranty-devices/import`, {
        devices: nodesForImport.map((node) => ({
          serviceTag: node.serviceTag,
        })),
      })
      .pipe(
        tap(() => {
          this.userDataStore.patchUserData();
          this.refresh();
        }),
      );
  }

  openTransferSiteModal(newSite: NewSiteInterface): void {
    this.formService.openForm({
      data: {
        title: $localize`:@@transferSiteModalTitle:Transfer site?`,
        form: {
          ids: ['info'],
          controls: {
            info: {
              title: newSite.serviceTag
                ? $localize`:@@transferSiteModalServiceTagWarning:Site with service tag <b>${new ServiceCodePipe().transform(newSite.serviceTag)}</b> will be deleted from another account and added to yours`
                : $localize`:@@transferSiteModalVendorDNSWarning:Site with ${environment.vendor?.dnsName}:VENDOR_DNS_NAME: address <b>${newSite.keenDns}</b> will be deleted from another account and added to yours`,
              type: FormControlTypeEnum.INFO_WARNING,
            },
          },
        },
        actions: [
          {
            type: FormButtonTypeEnum.SECONDARY,
          },
          {
            type: FormButtonTypeEnum.PRIMARY,
            action: () => this.transferSite(newSite),
          },
        ],
      },
    });
  }

  private transferSite(newSite: NewSiteInterface): Observable<unknown> {
    return this.http
      .put(`${environment.apiHost}/sites`, {
        ...newSite,
        force: true,
      })
      .pipe(
        tap(() => {
          this.refresh();
          this.showSuccessNotification(newSite);
          this.userDataStore.fetchUserData();
        }),
      );
  }

  private showSuccessNotification(value: NewSiteInterface): void {
    this.toastrService.success(
      value.serviceTag
        ? $localize`:@@addSiteWithServiceTagSuccessNotification:Site with service tag <b>${new ServiceCodePipe().transform(value.serviceTag)}</b> has been added to your account`
        : $localize`:@@addSiteWithVendorDNSAddressSuccessNotification:Site with ${environment.vendor?.dnsName}:VENDOR_DNS_NAME: address <b>${value.keenDns}</b> has been added to your account`,
    );
  }
}
